import { requirejsOne } from '@ds/swymkit/tools'

import { getPlatformId } from '~/features-shared/config-manager/stores/swym-config'
import { logger } from '~/features-shared/libs/utils/logger'
import type {
  IRTInterface,
  IRTInterfaceResponseData,
  IRTInterfaceResponseEvent,
  IRTInterfaceSendOptions,
  TxID,
} from '~/features-shared/rtconv/interfaces/rtinterface'

/*
 * This module uses the RTInterface module to communicate with RTConversation server.
 * For more information, refer to the RTInterface documentation.
 */

// This label is used to identify the app withing RTInterface, so that other apps using the API
// do not trigger TxID events on behalf of us. You can change however you want as long as it does not
// collide with another app's identifier.
const TxIDLABEL = '3DSwym'

// This indicates the default timeout value to wait for the websocket event response
// To be used by default for all websocket exchanges
const WebsocketEventDefaultTimeout = 3e4

/**
 * Requires RTInterface API.
 * @returns the RTInterface module.
 */
async function getRTInterface(): Promise<IRTInterface> {
  const RTInterface = await requirejsOne<IRTInterface>('DS/RTInterface/RTInterface')
  return RTInterface
}

/**
 * Returns connection status to RTC server.
 * @returns True if the browser is connected to RTC server.
 */
async function isConnected(): Promise<boolean> {
  const RTInterface = await getRTInterface()
  return RTInterface.isConnected()
}

/**
 * Sends the payload to RTC server via PlatformAPI.
 *
 * Two modes: synchronous and asynchronous. See below.
 *
 * Transaction ID mechanism: RTC uses websockets, an asynchronous protocol. TxIDs allow to
 * keep track of which request a response corresponds to. TxIDs are generated by RTInterface.
 *
 * ! Be mindful when using asynchronous sends.
 * Synchronous sends should be preferred to avoid blocking the execution flow when it is
 * unneeded and hampering performance.
 *
 * @param options - Options.
 * @param options.payload - Data to send to RTC server.
 * @param options.waitForResponse - True if asynchronous send, false otherwise. Default: false.
 * @param options.channel - PlatformAPI channel to send the data on.
 * @param options.timeout - timeout value to wait for promise resolve, else default value will be used
 * If unspecified, RTInterface will use default channel 'toRTC' (which is what you want for
 * all conversation events).
 *
 * @returns The TxID associated to the request if synchronous send.
 * The Promise that resolves with RTC response's raw data and rejects on error or timeout.
 */
async function send<T>({ payload, waitForResponse, channel, timeout }: {
  payload: IRTInterfaceSendOptions
  waitForResponse?: boolean
  channel?: string
  timeout?: number
}): Promise<T | TxID> {
  const RTInterface = await getRTInterface()
  if (!payload)
    return Promise.reject(new Error('RTInterfaceAPI send] RTInterface send error: missing payload'))

  // [Compass Multigeo] Retrieve current tenant
  const tenant = payload.tenant || getPlatformId()

  if (!waitForResponse) {
    // Synchronous send:
    // Sends the data and returns immediately after.
    // When to use: when you don't need to block the execution flow of your function, e.g. the user's action
    // doesn't block any feature.
    const data = RTInterface.send({ ...payload, tenant }, channel)
    if (data?.error)
      return Promise.reject(new Error(`[RTInterfaceAPI send] RTInterface send error: ${data.error}`))
    if (!data?.TxID)
      return Promise.reject(new Error('[RTInterfaceAPI send] RTInterface send error: missing TxID'))

    return data.TxID
  }
  else {
    // Asynchronous send:
    // Sends the data and returns a Promise that resolves when a response with the same TxID is received,
    // and rejects upon error or timeout.
    // When to use: when you need to interrupt the execution flow of your function and wait for the result
    // before continuing, e.g. the data returned by RTC is needed to proceed.
    return RTInterface.sendAsync(
      { ...payload, tenant },
      TxIDLABEL,
      channel,
      timeout || WebsocketEventDefaultTimeout
    )
  }
}

/**
 * Attachs a callback to the given event.
 * @param responseEventName - Event to listen to.
 * @param callback - Function to call upon reception of event.
 */
async function addCallback(
  responseEventName: IRTInterfaceResponseEvent,
  callback: (payload: IRTInterfaceResponseData) => void,
): Promise<void> {
  if (!callback || !(callback instanceof Function))
    return logger.error('[RTInterfaceAPI addCallback] RTInterface addCallback error: missing callback function}')
  getRTInterface()
    .then(async (RTInterface) => {
      const addCallbackResponse = await RTInterface.addCallback(
        (response) => {
          if (!response)
            return logger.error('[RTInterfaceAPI addCallback] Missing response')
          if (response.evenement !== responseEventName)
            return logger.error(`[RTInterfaceAPI addCallback] Received non-${responseEventName} event`)
          if (!response.data)
            return logger.error('[RTInterfaceAPI addCallback] Missing response.data')

          callback(response.data)
        },
        getPlatformId(),
        null,
        responseEventName,
      )
      if (addCallbackResponse instanceof Object)
        logger.error(`[RTInterfaceAPI addCallback] RTInterface addCallback error: ${addCallbackResponse.error}`)
    })
}

/**
 * Triggers the TxID event listener which resolves the promise awaiting in asynchronous send.
 * The promise will resolve with provided data or reject with provided error.
 *
 * ! Always use in combination with asynchronous sends (RTInterface.sendAsync).
 * Useless for synchronous sends (RTInterface.send).
 *
 * @param options - Options.
 * @param options.TxID - Transaction identifier generated by RTInterface.
 * @param options.data - Data received from RTC server.
 * @param options.error - Error received from RTC server.
 */
async function triggerTxIDEvent({ TxID, data, error }: { TxID: string, data?: any, error?: string }) {
  getRTInterface()
    .then(async RTInterface => RTInterface.triggerTxIDEvent(
      TxID,
      data,
      error,
      TxIDLABEL,
    ))
}

/**
 * Attachs a reconnect callback to call on RTC websocket reconnect.
 * @param callback - Function to call upon websocket reconnect.
 */
function addReconnectCallback(callback: (lastDisconnectTime: number) => void) {
  getRTInterface()
    .then(async RTInterface => RTInterface.addReconnectCallback(callback))
}

/**
 * RTInterface module APIs.
 */
export const RTInterfaceAPI = {
  isConnected,
  send,
  addCallback,
  triggerTxIDEvent,
  addReconnectCallback,
}
